import { useQuery } from '@tanstack/react-query';

import { http } from '@/shared/api/httpClient';
import { unwrapEnvelopeData } from '@/shared/api/apiEnvelope';
import { env } from '@/shared/config/env';

import type { CustomerListParams, CustomerListResult, CustomerRow } from '../types';
import { mapRemoteCustomer } from '../lib/mapRemoteCustomer';

import { customerKeys } from './keys';

function clampPositiveInt(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const n = Math.floor(value);
    return n > 0 ? n : fallback;
  }
  return fallback;
}

export async function fetchCustomersList(params?: CustomerListParams): Promise<CustomerListResult> {
  const base = `${env.adminProfilesPath.replace(/\/$/, '')}/customers`;
  const search = params?.search?.trim();
  const res = await http.get<unknown>(base, {
    params: {
      page: params?.page ?? 1,
      limit: params?.limit ?? 20,
      ...(search && search.length > 0 ? { search } : {}),
    },
  });
  const unwrapped = unwrapEnvelopeData<unknown>(res.data, res.status);
  const o = unwrapped as Record<string, unknown>;
  const itemsRaw = Array.isArray(o.items) ? o.items : [];
  const items = itemsRaw.map(mapRemoteCustomer).filter((r): r is CustomerRow => r !== null);
  const total = typeof o.total === 'number' && Number.isFinite(o.total) ? o.total : items.length;
  const page = clampPositiveInt(o.page, params?.page ?? 1);
  const limit = clampPositiveInt(o.limit, params?.limit ?? 20);
  const pageCountRaw = o.pageCount ?? o.page_count ?? o.totalPages ?? o.total_pages;
  const pageCount =
    typeof pageCountRaw === 'number' && Number.isFinite(pageCountRaw) && pageCountRaw > 0
      ? pageCountRaw
      : Math.max(1, Math.ceil(total / limit));

  return { items, total, page, limit, pageCount };
}

export function useCustomersQuery(
  params: CustomerListParams | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: customerKeys.list(params ?? {}),
    queryFn: () => fetchCustomersList(params),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
    retry: 1,
  });
}
